/**
 * PolicyHistoryModal - View Policy Change History
 *
 * Displays a timeline of policy changes with details about what changed and who made the change.
 */

import { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Alert, Card, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/apiClient';
import type { Policy, PolicyHistoryEntry } from '@/types/policy';
import { getErrorMessage } from '@/utils/errorHandling';
import RefreshButton from '@/components/common/RefreshButton';

interface PolicyHistoryModalProps {
  show: boolean;
  onHide: () => void;
  policy: Policy | null;
}

export default function PolicyHistoryModal({
  show,
  onHide,
  policy,
}: PolicyHistoryModalProps) {
  const { t } = useTranslation();

  const [history, setHistory] = useState<PolicyHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());

  const loadHistory = useCallback(async () => {
    if (!policy) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.getPolicyHistory(policy.id);

      if (response.error) {
        setError(response.error);
        setIsLoading(false);
        return;
      }

      const historyData = response.data as { entries?: PolicyHistoryEntry[] } | undefined;
      if (historyData?.entries) {
        setHistory(historyData.entries);
      }

      setIsLoading(false);
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to load policy history');
      setIsLoading(false);
    }
  }, [policy]);

  useEffect(() => {
    if (show && policy) {
      loadHistory();
    }
  }, [show, policy, loadHistory]);

  const toggleExpand = (entryId: number) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };

  const getChangeTypeBadge = (changeType: string) => {
    switch (changeType) {
      case 'created':
        return <Badge bg="success">Created</Badge>;
      case 'updated':
        return <Badge bg="primary">Updated</Badge>;
      case 'deleted':
        return <Badge bg="danger">Deleted</Badge>;
      default:
        return <Badge bg="secondary">{changeType}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <Modal.Title>
            {t('admin.policyManagement.policyHistory', 'Policy Change History')}
          </Modal.Title>
          <RefreshButton onRefresh={loadHistory} size="sm" variant="outline-secondary" ariaLabel="Refresh history" />
        </div>
      </Modal.Header>

      <Modal.Body>
        {policy && (
          <Alert variant="info">
            <strong>{t('admin.policyManagement.policy', 'Policy')}:</strong> {policy.name}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Alert variant="info">
            {t('admin.policyManagement.loadingHistory', 'Loading history...')}
          </Alert>
        ) : history.length === 0 ? (
          <Alert variant="info">
            {t('admin.policyManagement.noHistory', 'No change history available for this policy.')}
          </Alert>
        ) : (
          <div className="policy-history-timeline">
            {history.map((entry) => (
              <Card key={entry.id} className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    {getChangeTypeBadge(entry.change_type)}
                    <span className="ms-2 text-muted small">
                      {formatDate(entry.changed_at)}
                    </span>
                  </div>
                  <div className="text-muted small">
                    {entry.changed_by
                      ? t('admin.policyManagement.changedBy', `By user ID: ${entry.changed_by}`)
                      : t('admin.policyManagement.systemChange', 'System change')}
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="mb-2">
                    <strong>{t('admin.policyManagement.name', 'Name')}:</strong> {entry.policy_name}
                  </div>
                  {entry.policy_description && (
                    <div className="mb-2">
                      <strong>{t('admin.policyManagement.description', 'Description')}:</strong>{' '}
                      {entry.policy_description}
                    </div>
                  )}
                  <div className="mb-2">
                    <strong>{t('admin.policyManagement.version', 'Version')}:</strong>{' '}
                    {entry.policy_version}
                  </div>
                  <div className="mb-2">
                    <strong>{t('admin.policyManagement.statements', 'Statements')}:</strong>{' '}
                    {entry.policy_document.Statement.length}
                  </div>

                  {/* Expandable Policy Document */}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => toggleExpand(entry.id)}
                  >
                    {expandedEntries.has(entry.id)
                      ? t('admin.policyManagement.hideDocument', 'Hide Policy Document')
                      : t('admin.policyManagement.viewDocument', 'View Policy Document')}
                  </Button>

                  {expandedEntries.has(entry.id) && (
                    <div className="mt-3">
                      <pre
                        className="bg-light p-3 rounded"
                        style={{
                          maxHeight: '300px',
                          overflowY: 'auto',
                          fontSize: '0.85em',
                        }}
                      >
                        {JSON.stringify(entry.policy_document, null, 2)}
                      </pre>
                    </div>
                  )}
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('admin.policyManagement.close', 'Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
